import { ProjectErrorComponent } from "@/components/error/project-error";
import { containerQueries } from "@/features/containers";
import ProjectSidebar from "@/features/projects/components/project-sidebar";
import { type ProjectAccess, projectAccessFromRole } from "@/features/projects/project-access";
import { IncognitoSupportBanner } from "@/features/superuser/incognito/components/incognito-support-banner";
import { loadIncognitoProject } from "@/features/superuser/incognito/incognito-route-guard";
import { IncognitoProjectSidebar } from "@/features/superuser/incognito/project/incognito-project-sidebar";
import { WorkspaceCreditWarningBanner } from "@/features/workspaces/components/workspace-credit-warning-banner";
import { isContributorProjectPathAllowed } from "@/features/workspaces/contributor-route";
import { useResetScrollOnPathChange } from "@/hooks/use-reset-scroll-on-path-change";
import { ACTIVE_PROJECT_SLUG_KEY } from "@/lib/local-storage.consts";
import { recordRecentProjectPath } from "@/lib/recent-containers";
import { cn } from "@/lib/utils";
import type { Project } from "@shared/types/supabase.types";
import type { QueryClient } from "@tanstack/react-query";
import { Outlet, createFileRoute, redirect, useMatchRoute } from "@tanstack/react-router";
import { useRef } from "react";

export const Route = createFileRoute("/_authenticated/workspaces/$slug/projects/$projectSlug")({
  beforeLoad: async ({
    context,
    params,
    location,
    cause,
  }): Promise<{ project: Project; projectAccess: ProjectAccess }> => {
    const workspaceSlug = params.slug;
    const projectSlug = params.projectSlug;
    // CR-165 D-E: the incognito path reads through the admin-only RPC and
    // writes nothing (no recent path, no active project slug).
    if (context.workspaceAccess.mode === "incognito") {
      const { project } = await loadIncognitoProject({
        workspaceSlug,
        projectSlug,
        queryClient: context.queryClient,
      });
      return { project, projectAccess: "member" };
    }
    recordRecentProjectPath(workspaceSlug, projectSlug);

    let project: Project;
    try {
      project = await context.queryClient.ensureQueryData(
        containerQueries.projectBySlugs(workspaceSlug, projectSlug),
      );
    } catch (error) {
      localStorage.removeItem(ACTIVE_PROJECT_SLUG_KEY);
      // CR-203: the project list shows every workspace project, so a project
      // the caller may see but has not joined lands on its join/request card.
      throw redirect({
        to: "/workspaces/$slug/projects",
        params: { slug: workspaceSlug },
      });
    }

    const projectAccess = await resolveProjectAccess(
      context.queryClient,
      context.user.id,
      project.id,
      context.workspaceAccess.mode === "contributor",
      cause === "preload",
    );

    // CR-183 D8: a contributor's only project page is the suggestions page
    // (this also covers the bare project root). A workspace member who is a
    // contributor on this project (D9) is narrowed the same way.
    if (
      projectAccess === "contributor" &&
      !isContributorProjectPathAllowed(location.pathname, workspaceSlug, projectSlug)
    ) {
      throw redirect({
        to: "/workspaces/$slug/projects/$projectSlug/suggestions",
        params: { slug: workspaceSlug, projectSlug },
        replace: true,
      });
    }

    return { project, projectAccess };
  },
  component: ProjectLayout,
  errorComponent: ProjectErrorComponent,
});

/**
 * CR-183: the caller's access mode on this project, from their own membership
 * row (the same cached `ensure_container_member` read the sidebar uses). A
 * workspace contributor is a contributor everywhere; otherwise a failed read
 * keeps the member layout, whose reads RLS already guards. A hover preload
 * skips the read: `ensure_container_member` inserts on a miss, and a preload
 * must not join anyone (the real navigation re-runs this guard).
 */
async function resolveProjectAccess(
  queryClient: QueryClient,
  userId: string,
  projectId: string,
  isWorkspaceContributor: boolean,
  isPreload: boolean,
): Promise<ProjectAccess> {
  if (isWorkspaceContributor) {
    return "contributor";
  }
  if (isPreload) {
    return "member";
  }
  try {
    const membership = await queryClient.ensureQueryData({
      ...containerQueries.membershipByUserAndContainer(userId, projectId),
      revalidateIfStale: true,
    });
    return projectAccessFromRole(membership?.role);
  } catch {
    return "member";
  }
}

function ProjectLayout() {
  const { project, projectAccess, workspace, workspaceAccess } = Route.useRouteContext();
  const matchRoute = useMatchRoute();
  const workspaceId = project.parent_container_id;
  // The content pane below is the only vertical scroller of every project screen and
  // survives navigations between them; start each screen at the top (CR-143).
  const contentRef = useRef<HTMLDivElement>(null);
  useResetScrollOnPathChange(contentRef);

  // CR-181: every project-creation step (create/invite, create/docs, …) is a
  // full-width wizard screen, so the sidebar is hidden on the whole create/* subtree.
  const isCreateRoute = !!matchRoute({
    to: "/workspaces/$slug/projects/$projectSlug/create",
    fuzzy: true,
  });
  // CR-171: the chat page owns its scroller — its composer floats at the bottom of its
  // own message list — so on a chat route the column below is sized to the pane and the
  // page fills whatever the credit banner leaves (the pane itself never scrolls there).
  const isChatRoute = !!matchRoute({
    to: "/workspaces/$slug/projects/$projectSlug/chats/$chatId",
  });

  // CR-236: every page scroller below is `relative` so it is the containing block of
  // any out-of-flow descendant left without a positioned ancestor (e.g. an `sr-only`
  // span, which is position:absolute). Otherwise such an element escapes the scroller,
  // stretches the document, and the whole window scrolls under the app shell.
  // CR-165: the member sidebar and the credit banner mount member-only reads
  // and mutations; the incognito view gets its own display-only sidebar.
  if (workspaceAccess.mode === "incognito") {
    return (
      <div className="flex h-[calc(100vh-4rem)] overflow-hidden" key={project.id}>
        <IncognitoProjectSidebar />
        <div ref={contentRef} className="relative flex-1 overflow-y-auto">
          <IncognitoSupportBanner workspaceName={workspace.name ?? workspace.slug ?? ""} />
          <div className="flex flex-col">
            <Outlet />
          </div>
        </div>
      </div>
    );
  }

  // CR-183 D8: a contributor has one page (suggestions) and no sidebar; the
  // credit banner reads workspace billing they cannot see.
  if (projectAccess === "contributor") {
    return (
      <div
        className="relative h-[calc(100vh-4rem)] overflow-y-auto"
        key={project.id}
        ref={contentRef}
      >
        <Outlet />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden" key={project.id}>
      {!isCreateRoute && <ProjectSidebar />}

      <div ref={contentRef} className="relative flex-1 overflow-y-auto">
        <div className={cn("flex flex-col", isChatRoute && "h-full")}>
          <WorkspaceCreditWarningBanner workspaceId={workspaceId} className="mx-6 mt-6" />
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export const ProjectRoute = Route;
